import React from "react";
import {
  AssetType,
  SeatingMetadata,
  AssetFormData,
} from "../../../types/asset-types";
import {
  BaseAssetFormProps,
  AssetFormHeader,
  FormField,
  TextInput,
  NumberInput,
  CheckboxInput,
  AIConversationFields,
} from "./asset-form-base";

export function SeatingAssetForm({
  data,
  onChange,
  errors,
  disabled,
}: BaseAssetFormProps) {
  const metadata = (data.metadata as Partial<SeatingMetadata>) || {};

  const handleMetadataChange = (field: keyof SeatingMetadata, value: any) => {
    const newMetadata = {
      ...metadata,
      [field]: value,
    };
    onChange({
      ...data,
      metadata: newMetadata,
    });
  };

  const handleAIContentChange = (aiData: any) => {
    const newMetadata = {
      ...metadata,
      ...aiData,
    };
    onChange({
      ...data,
      metadata: newMetadata,
    });
  };

  // Auto-calculate seat count when start/end change
  const handleSeatRangeChange = (
    field: "seatStart" | "seatEnd",
    value: string
  ) => {
    const newMetadata = {
      ...metadata,
      [field]: value,
    };

    // Try to calculate seat count if both start and end are provided
    if (newMetadata.seatStart && newMetadata.seatEnd) {
      const startNum = parseInt(newMetadata.seatStart.replace(/\D/g, ""));
      const endNum = parseInt(newMetadata.seatEnd.replace(/\D/g, ""));

      if (!isNaN(startNum) && !isNaN(endNum) && endNum >= startNum) {
        newMetadata.seatCount = endNum - startNum + 1;
      }
    }

    onChange({
      ...data,
      metadata: newMetadata,
    });
  };

  return (
    <div className="space-y-6">
      <AssetFormHeader
        type={AssetType.SEATING}
        description="Create seating assets for venues like stadiums, theaters, or arenas with specific seat ranges and sections."
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField
          label="Section"
          required
          error={errors?.["metadata.section"]}
          description="Section identifier (e.g., A, 101, Lower Bowl)"
        >
          <TextInput
            value={metadata.section || ""}
            onChange={(value) => handleMetadataChange("section", value)}
            placeholder="Section A, 101, Lower Bowl"
            disabled={disabled}
            required
          />
        </FormField>

        <FormField
          label="Level"
          required
          error={errors?.["metadata.level"]}
          description="Level or tier (e.g., Lower, Upper, Mezzanine)"
        >
          <TextInput
            value={metadata.level || ""}
            onChange={(value) => handleMetadataChange("level", value)}
            placeholder="Lower Level, Upper Deck, Mezzanine"
            disabled={disabled}
            required
          />
        </FormField>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <FormField
          label="Seat Start"
          required
          error={errors?.["metadata.seatStart"]}
          description="First seat in the range"
        >
          <TextInput
            value={metadata.seatStart || ""}
            onChange={(value) => handleSeatRangeChange("seatStart", value)}
            placeholder="1, A1, Row 1 Seat 1"
            disabled={disabled}
            required
          />
        </FormField>

        <FormField
          label="Seat End"
          required
          error={errors?.["metadata.seatEnd"]}
          description="Last seat in the range"
        >
          <TextInput
            value={metadata.seatEnd || ""}
            onChange={(value) => handleSeatRangeChange("seatEnd", value)}
            placeholder="20, A20, Row 1 Seat 20"
            disabled={disabled}
            required
          />
        </FormField>

        <FormField
          label="Seat Count"
          required
          error={errors?.["metadata.seatCount"]}
          description="Total number of seats"
        >
          <NumberInput
            value={metadata.seatCount}
            onChange={(value) => handleMetadataChange("seatCount", value)}
            placeholder="20"
            disabled={disabled}
            required
            min={1}
          />
        </FormField>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField
          label="Accessibility"
          error={errors?.["metadata.accessibility"]}
          description="Are these accessible/ADA compliant seats?"
        >
          <div className="flex items-center space-x-2">
            <CheckboxInput
              checked={metadata.accessibility || false}
              onChange={(value) => handleMetadataChange("accessibility", value)}
              disabled={disabled}
            />
            <span className="text-sm text-gray-700">
              These seats are accessible/ADA compliant
            </span>
          </div>
        </FormField>

        <FormField
          label="Premium"
          error={errors?.["metadata.premium"]}
          description="Are these premium/VIP seats?"
        >
          <div className="flex items-center space-x-2">
            <CheckboxInput
              checked={metadata.premium || false}
              onChange={(value) => handleMetadataChange("premium", value)}
              disabled={disabled}
            />
            <span className="text-sm text-gray-700">
              These are premium/VIP seats
            </span>
          </div>
        </FormField>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start">
          <div className="flex-shrink-0">
            <svg
              className="h-5 w-5 text-blue-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <div className="ml-3">
            <h4 className="text-sm font-medium text-blue-800">
              Bulk Seating Creation
            </h4>
            <p className="mt-1 text-sm text-blue-700">
              For large seating sections, consider using the bulk creation
              feature to automatically generate multiple seat assets with
              sequential numbering.
            </p>
          </div>
        </div>
      </div>

      <AIConversationFields
        data={metadata}
        onChange={handleAIContentChange}
        errors={errors}
        disabled={disabled}
      />
    </div>
  );
}
